/**
 * Save operation utilities for canvas templates
 * Extracted from AddCanvasTemplate.js to reduce file size
 */
import { openCanvasDB } from "../../../util/indexedDBUtils";

const isOffCanvasElement = (element = {}) =>
  element.isOffCanvas === true ||
  element.is_off_canvas === true ||
  element.isOffcanvas === true;

export const mergeMainAndOffCanvasElements = (
  mainElements = {},
  offCanvasElements = {},
) => {
  const mergeElementArrays = (mainArr, offArr) => [
    ...(Array.isArray(mainArr) ? mainArr : []),
    ...(Array.isArray(offArr)
      ? offArr.map((item) => ({
          ...item,
          isOffCanvas: true,
          isOffcanvas: true,
        }))
      : []),
  ];

  return {
    ...(mainElements || {}),
    texts: mergeElementArrays(mainElements?.texts, offCanvasElements?.texts),
    rectangles: mergeElementArrays(
      mainElements?.rectangles,
      offCanvasElements?.rectangles,
    ),
    circles: mergeElementArrays(mainElements?.circles, offCanvasElements?.circles),
    arrows: mergeElementArrays(mainElements?.arrows, offCanvasElements?.arrows),
    lines: mergeElementArrays(mainElements?.lines, offCanvasElements?.lines),
    doubleSidedArrows: mergeElementArrays(
      mainElements?.doubleSidedArrows,
      offCanvasElements?.doubleSidedArrows,
    ),
    scribbles: mergeElementArrays(
      mainElements?.scribbles,
      offCanvasElements?.scribbles,
    ),
    images: mergeElementArrays(mainElements?.images, offCanvasElements?.images),
    videos: mergeElementArrays(mainElements?.videos, offCanvasElements?.videos),
  };
};

/**
 * Process shapes and add them to shapeData array
 * @param {Array} arr - Array of shape elements
 * @param {string} type - Shape type (Circle, Arrow, etc.)
 * @param {Array} shapeData - Array to push processed shapes to
 */
export const pushShapes = (arr, type, shapeData) => {
  if (!arr || !Array.isArray(arr)) return;
  arr.forEach((el) => {
    const shapeFill = el.fill || el.fillColor || "";
    const shapeColor = el.stroke || el.strokeColor || "";

    const getColorValue = (color) => {
      if (!color || color === "transparent") return 0;
      if (color.startsWith("#")) return parseInt(color.replace("#", ""), 16);
      if (color.startsWith("rgb")) {
        const matches = color.match(/\d+/g);
        if (matches && matches.length >= 3) {
          const [r, g, b] = matches;
          return (r << 16) | (g << 8) | b;
        }
      }
      return 0;
    };

    const fillValue = getColorValue(shapeFill);
    const strokeValue = getColorValue(shapeColor);

    // Check if shape_id or id starts with "marker"
    const isMarkerShape =
      (el.shape_id && el.shape_id.includes("marker")) ||
      (el.id && el.id.includes("marker"));
    const elementIsOffCanvas = isOffCanvasElement(el);
    const normalizedElement = { ...el };
    delete normalizedElement.isOffCanvas;
    delete normalizedElement.is_off_canvas;
    delete normalizedElement.isOffcanvas;

    const baseShapeData = {
      height: Math.round(Number(el.height) || 0),
      width: Math.round(Number(el.width) || 0),
      shape_fill: fillValue,
      shape_color: strokeValue,
      stroke_color: strokeValue,
      stroke_size: Math.round(Number(el.strokeWidth) || 0),
      shape_info: {
        ...normalizedElement,
        fill: shapeFill,
        stroke: shapeColor,
        strokeColor: strokeValue,
        ...(elementIsOffCanvas ? { isOffcanvas: true } : {}),
      },
      shape_properties: {
        ...normalizedElement,
        fill: shapeFill,
        stroke: shapeColor,
        strokeColor: strokeValue,
        ...(elementIsOffCanvas ? { isOffcanvas: true } : {}),
      },
      type,
    };

    // For Text type, ensure canvas_page_location_and_size with rotation is included
    if (type === "Text") {
      baseShapeData.canvas_page_location_and_size = {
        x: el.x ?? 0,
        y: el.y ?? 0,
        width: el.width ?? 200,
        height: el.height ?? 100,
        rotation: el.rotation ?? 0,
      };
      baseShapeData.shape_properties.canvas_page_location_and_size = {
        x: el.x ?? 0,
        y: el.y ?? 0,
        width: el.width ?? 200,
        height: el.height ?? 100,
        rotation: el.rotation ?? 0,
      };
      baseShapeData.shape_info.canvas_page_location_and_size = {
        x: el.x ?? 0,
        y: el.y ?? 0,
        width: el.width ?? 200,
        height: el.height ?? 100,
        rotation: el.rotation ?? 0,
      };
    }

    // Only add shape_id if it's not a marker shape
    if (!isMarkerShape) {
      baseShapeData.shape_id = el.shape_id || el.id;
      baseShapeData.shape_info.shape_id = el.shape_id || el.id;
      baseShapeData.shape_properties.shape_id = el.shape_id || el.id;
    }

    shapeData.push(baseShapeData);
  });
};

/**
 * Fetch removed IDs (shapes, texts, images) from IndexedDB for this page/template
 * @param {string} pageKey - Page key identifier
 * @returns {Promise<Object>} Object with shapeIds, textIds, textContentIds, imageIds arrays
 */
export const getRemovedIdsForTemplatePage = async (pageKey) => {
  const empty = {
    shapeIds: [],
    textIds: [],
    textContentIds: [],
    imageIds: [],
  };
  if (!pageKey) return empty;
  try {
    const db = await openCanvasDB();
    const removed = await new Promise((resolve, reject) => {
      const tx = db.transaction(["canvasElements"], "readonly");
      const store = tx.objectStore("canvasElements");
      const getReq = store.get(`${pageKey}_removed_shapes`);
      getReq.onsuccess = () => resolve(getReq.result);
      getReq.onerror = () => reject(getReq.error);
    });
    if (!removed?.data) return empty;
    const allShapeIds = [];
    const allTextIds = [];
    const allTextContentIds = [];
    const allImageIds = [];
    Object.entries(removed.data).forEach(([key, arr]) => {
      if (!Array.isArray(arr)) return;
      allShapeIds.push(...arr);
      if (key === "removedTexts") {
        allTextIds.push(...arr);
        allTextContentIds.push(...arr);
      } else if (key === "removedImages") {
        allImageIds.push(...arr);
      }
    });
    const uniq = (xs) =>
      [...new Set(xs)].filter((id) => id && String(id).trim() !== "");
    return {
      shapeIds: uniq(allShapeIds),
      textIds: uniq(allTextIds),
      textContentIds: uniq(allTextContentIds),
      imageIds: uniq(allImageIds),
    };
  } catch (e) {
    return empty;
  }
};

/**
 * Deduplicate elements by ID
 * @param {Array} arr - Array of elements
 * @returns {Array} Deduplicated array
 */
export const dedupeById = (arr) =>
  Array.isArray(arr)
    ? Array.from(
        new Map(
          arr.map((el) => [el?.id || el?.shape_id || el?.image_id, el])
        ).values()
      )
    : [];

/**
 * Deduplicate elements by name
 * @param {Array} arr - Array of elements
 * @returns {Array} Deduplicated array
 */
export const deduplicateByName = (arr) =>
  Array.isArray(arr)
    ? arr.filter(
        (item, idx, self) => idx === self.findIndex((a) => a.name === item.name)
      )
    : arr;
